<template>
  <div class="dd-wrapper">
    <div
      v-if="storesError.error"
      class="margin-top-xs margin-bottom-xs"
      aria-live="assertive"
    >
      <Notification
        severity="error"
        @close="onCloseError"
      >
        {{ storesError.message }}
      </Notification>
    </div>

    <div
      v-if="areaRugItems && !storesError.error"
      class="margin-top-xs margin-bottom-xs"
    >
      <Notification
        severity="info"
        :closable="false"
      >
        {{ AREA_RUG_INFO }}
      </Notification>
    </div>

    <div class="grid-x">
      <div class="cell small-12">
        <div class="input-container">
          <div class="input-inner-start">
            <div class="search-large" />
          </div>
          <input
            v-if="searchByZipCode"
            ref="inputSearch"
            v-model="keyword"
            name="locatorSearch"
            class="input-search"
            autocomplete="off"
            placeholder="Enter a zip code"
            type="text"
            @keyup.enter="searchLocationsByZipCode"
          >
          <!-- TODO: Use https://ui-platform.staging.devops.fds.com/atomic-ui/search component for future story, so some of these code/css can be removed. -->
          <div
            v-if="searchByZipCode"
            class="input-inner-end search-input-cta-container"
          >
            <button
              v-if="keyword?.length"
              class="clear-filled search-input-cta"
              aria-label="clear search"
              @click="clearHandler"
            />

            <button
              class="margin-left-xxs medium search-input-cta search-button"
              :class="isMCOM ? 'is-mcom' : ''"
              aria-label="Submit"
              @click="searchLocationsByZipCode"
            >
              Go
            </button>
          </div>
          <input
            v-if="!searchByZipCode"
            ref="inputSearch"
            v-model="keyword"
            class="input-search"
            placeholder="Search by address or zip"
            type="text"
            autocomplete="off"
            @keyup.enter="setGeoLocation"
            @keyup.down="onArrowDown"
            @keyup.up="onArrowUp"
            @keydown="isOpen = true"
          >
          <div
            v-if="!searchByZipCode"
            class="input-inner-end search-input-cta-container"
          >
            <!-- <button
              v-if="!keyword?.length"
              class="location-arrow"
              aria-label="Search by current location"
              @click="getGeoLocation"
            /> -->
            <button
              v-if="keyword?.length"
              class="clear-filled search-input-cta"
              aria-label="clear search"
              @click="clearHandler"
            />

            <button
              class="margin-left-xxs medium search-input-cta search-button"
              :class="isMCOM ? 'is-mcom' : ''"
              aria-label="Submit"
              @click="setGeoLocation"
            >
              Go
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="disableLocationError"
      class="margin-top-xs margin-bottom-xs"
    >
      <Notification severity="error">
        {{ LOCATION_ERROR_NOT_ENABLED }}
      </Notification>
    </div>
    <div
      v-if="!isValidZipcode"
      class="margin-top-xs margin-bottom-xs"
      aria-live="assertive"
    >
      <Notification severity="error">
        {{ LOCATION_INVALID_ZIPCODE }}
      </Notification>
    </div>
    <div
      v-if="places?.length > 3 && isOpen"
      id="auto-select-component"
      class="grid-x dd-list"
    >
      <div
        v-for="(result, index) in places"
        :key="index"
        class="cell dd-item"
        :class="{ 'is-active': index === arrowCounter }"
        @click="setLocation(result, index)"
      >
        {{ result.description }}
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { mapActions, mapState, mapMutations } from 'vuex';
import Notification from '@atomic-ui/notification';
import {
  FETCH_LOCATIONS,
  FETCH_GEOCODE,
  RESET_STORE_ACTION,
  FETCH_STORES_BY_ZIPCODE,
  FETCH_LOCATIONS_ADDRESS,
} from '../types/actions';
import { SET_LOCATIONS, SET_STORES, SET_STORES_ERROR } from '../types/mutations';
import {
  LOCATION_ERROR_NOT_ENABLED, LOCATION_INVALID_ZIPCODE, AREA_RUG_INFO, ERROR_MESSAGES,
} from '../types/constants';

export default {
  name: 'SearchBar',
  components: { Notification },
  props: {
    typeOf: {
      type: String,
      default: '',
    },
    useMspLocatorApi: {
      type: Boolean,
      default: false,
    },
    areaRugItems: {
      type: Boolean,
      default: false,
    },
    separateOutlets: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['zipcodeSubmitted'],
  data() {
    return {
      keyword: '',
      zipCode: 0,
      isValidZipcode: true,
      isOpen: true,
      arrowCounter: -1,
      disableLocationError: false,
      isMCOM: computed(() => process.env.ENTRY_POINT === 'mcom'),
    };
  },
  computed: {
    ...mapState('locations', ['prefilledZipCode', 'searchByZipCode', 'geoCode', 'places', 'storesError', 'page', 'searchLocationByAddress']),
  },
  watch: {
    geoCode(newVal) {
      if (newVal?.length) {
        const { formatted_address: formattedAddress } = newVal[0];
        this.keyword = formattedAddress;
      } else {
        this.keyword = '';
      }
    },
    keyword(input) {
      this.disableLocationError = false;
      this[SET_STORES_ERROR](null);
      if (input && !this.searchByZipCode) {
        if (this.searchLocationByAddress) {
          this[FETCH_LOCATIONS_ADDRESS]({ input });
        } else {
          this[FETCH_LOCATIONS]({ input });
        }
      }
    },
    prefilledZipCode(newVal) {
      if (this.searchByZipCode) {
        if (newVal) {
          this.keyword = newVal;
        } else {
          this.keyword = '';
        }
      }
    },
  },
  created() {
    this.LOCATION_ERROR_NOT_ENABLED = LOCATION_ERROR_NOT_ENABLED;
    this.LOCATION_INVALID_ZIPCODE = LOCATION_INVALID_ZIPCODE;
    this.AREA_RUG_INFO = AREA_RUG_INFO;
  },
  mounted() {
    document.addEventListener('click', this.close);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.close);
  },
  methods: {
    ...mapActions('locations', [
      FETCH_LOCATIONS,
      FETCH_GEOCODE,
      RESET_STORE_ACTION,
      FETCH_STORES_BY_ZIPCODE,
      FETCH_LOCATIONS_ADDRESS,
    ]),
    ...mapMutations('locations', [SET_LOCATIONS, SET_STORES, SET_STORES_ERROR]),
    getGeoLocation() {
      this.disableLocationError = false;
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(({ coords: coordinates }) => {
          const { latitude, longitude } = coordinates;
          this[FETCH_GEOCODE]({
            address: `${+latitude},${+longitude}`,
            locationType: this.typeOf,
            useMspLocatorApi: this.useMspLocatorApi,
            separateOutlets: this.separateOutlets,
          });
        }, () => {
          this[SET_STORES]([]);
          this.disableLocationError = true;
        });
      } else {
        this[SET_STORES]([]);
        this.disableLocationError = true;
      }
    },
    close(e) {
      if (!e.target.closest('#auto-select-component')) {
        this[SET_LOCATIONS]([]);
        this.arrowCounter = -1;
        this.isOpen = false;
      }
    },
    clearHandler() {
      this.keyword = '';
      this.$refs.inputSearch.focus();
    },
    setGeoLocation() {
      if (!this.keyword) {
        this[SET_STORES_ERROR]({ error: 40490, message: ERROR_MESSAGES[40490], messageEdit: false });
      } else {
        this[FETCH_GEOCODE]({
          address: (this.isOpen && this.arrowCounter > -1) ? this.places[this.arrowCounter].description : this.keyword,
          locationType: this.typeOf,
          useMspLocatorApi: this.useMspLocatorApi,
          separateOutlets: this.separateOutlets,
        });
        this.arrowCounter = -1;
        this.isOpen = false;
      }
    },
    setLocation(item) {
      this[FETCH_GEOCODE]({
        address: item.description,
        locationType: this.typeOf,
        useMspLocatorApi: this.useMspLocatorApi,
        separateOutlets: this.separateOutlets,
      });
      this.isOpen = false;
    },
    onArrowDown() {
      if (this.arrowCounter < 4) {
        this.arrowCounter += 1;
      }
    },
    onArrowUp() {
      if (this.arrowCounter > 0) {
        this.arrowCounter -= 1;
      }
    },
    searchLocationsByZipCode() {
      if (!this.keyword) {
        this[SET_STORES_ERROR]({ error: 40490, message: ERROR_MESSAGES[40490], messageEdit: false });
      } else {
        if (this.testZipcode(this.keyword)) {
          this.isValidZipcode = true;
          this[FETCH_STORES_BY_ZIPCODE]({ enteredZipCode: this.keyword });
        } else {
          this[SET_STORES]([]);
          this[SET_LOCATIONS]([]);
          this.isValidZipcode = false;
        }
        this.$emit('zipcodeSubmitted', { zipCode: this.keyword, isValidZipcode: this.isValidZipcode });
      }
    },
    onCloseError() {
      this[SET_STORES_ERROR](null);
    },
    testZipcode(zipcode) {
      return /\b\d{5}\b/.test(zipcode); // Test for exactly 5 digits, will not match the first 5 of a 6+ digit input
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@core/csg/src/scss/forms/forms.scss";
@import "~@core/csg/src/scss/encodedIcons/mcom/_error.scss";
@import "~@core/csg/src/scss/encodedIcons/mcom/_error-red.scss";
@import "~@core/csg/src/scss/encodedIcons/mcom/_search-large.scss";
@import "~@core/csg/src/scss/encodedIcons/mcom/_location-arrow.scss";
@import "~@core/csg/src/scss/encodedIcons/mcom/_clear-filled.scss";
@include csg-forms;

.dd-wrapper {
  position: relative;
}
.dd-list {
  position: relative;
  left: rem-calc(4);
  right: rem-calc(20);
  top: rem-calc(-7);
  z-index: 10;
  .dd-item {
    background: $white;
    cursor: pointer;
    padding: rem-calc(8);
    border: $gray-1-border;
    margin: 0;
    &:hover, &.is-active {
      background: $gray-4-color;
    }
  }
}
.search-input-cta-container{
  display: flex;
  align-items: center;
}
.search-input-cta{
  cursor: pointer;
}
.input-search {
  padding-right: 3.5rem;
}

.search-button::after {
  content: '';
  position: absolute;
  top: -11px;
  right: -8px;
  width: 35px; /* Adjust width as needed */
  height: 40px; /* Full height of the input */
  background-color: black; /* Set background to black */
  z-index: -1; /* Ensure it's below the button */
}
.is-mcom::after {
border-radius: 0px 50% 50% 0px;
}
.search-button {
  color: white;
}
.clear-filled {
  width: 20px;
  height: 20px;
  margin-right: 10px;
  background-size: 22px 22px;
}
</style>
